import { Alert, Group, Text, UnstyledButton } from "@mantine/core";
import { withClickAnalytics } from "@soluto-private/eventualize-react";
import { useMemo } from "react";
import { FlexSpacer } from "@/common-ui";
import { useActiveVoiceTask, useCallbackState } from "@/sdk";
import { useControlsStore } from "../../../CallControls/controls.store";
import styles from "./CallbackInfoBanner.module.css";

const AnalyticsAlertBanner = withClickAnalytics(Alert, "Click");

const AnalyticsChangeButton = withClickAnalytics(UnstyledButton<"button">, "Click");

export function CallbackInfoBanner() {
    const activeTask = useActiveVoiceTask();
    const { scheduleCallbackPanelOpen, openCallbackPanel } = useControlsStore();
    const callbackState = useCallbackState();

    // If the call is not in an active-state we cannot change the callback info
    const disableChange = useMemo(() => {
        return scheduleCallbackPanelOpen || ["cancelled", "completed", "wrapping"].includes(activeTask.status);
    }, [activeTask.status, scheduleCallbackPanelOpen]);
    const scheduledForText: string | null = useMemo(() => {
        if (!callbackState) return null;

        if (callbackState.callbackType === "CallbackNow" && callbackState.callbackDelay !== null) {
            if (callbackState.callbackDelay <= 1) return "immediately after the call ends";
            return `${callbackState.callbackDelay} seconds after the call ends`;
        }

        const scheduledTime = new Date(callbackState.scheduledFor);
        const formattedDate = new Intl.DateTimeFormat(navigator.languages[0], {
            dateStyle: "short",
            timeStyle: "short",
        }).format(scheduledTime);
        //TODO: format dates in a more readable way, ie. dayjs(scheduledTime).format("llll");
        return `for ${formattedDate}`;
    }, [callbackState]);

    if (!callbackState) {
        console.error("CallbackInfoBanner should not be rendered if there is no callback state");
        <Alert color="red.9" variant="filled">
            CallbackInfoBanner should not be rendered if there is no callback state
        </Alert>;
    }

    return (
        <AnalyticsAlertBanner
            analytics={{
                actionId: "CallbackInfoBanner",
            }}
            classNames={{
                root: styles.alertRoot,
            }}
            p="xs"
            pt="0"
            pb="0"
        >
            <Group justify="space-between" wrap={"nowrap"}>
                <Text
                    data-testid="call-controls-callback-delay-text"
                    size="xs"
                    fw="lighter"
                    lineClamp={1}
                    title={`A callback is scheduled ${scheduledForText}`}
                >
                    A callback is scheduled <b>{scheduledForText}</b>
                </Text>

                <FlexSpacer />

                {!scheduleCallbackPanelOpen || !disableChange ? (
                    <AnalyticsChangeButton
                        analytics={{
                            actionId: "CallbackChangeButton",
                        }}
                        color="gray.1"
                        data-testid="call-controls-callback-delay-edit"
                        disabled={disableChange}
                        fz="xs"
                        size="xs"
                        onClick={openCallbackPanel}
                        className={styles.editButton}
                    >
                        Edit
                    </AnalyticsChangeButton>
                ) : null}
            </Group>
        </AnalyticsAlertBanner>
    );
}
